import { formatDate, formatRelative, formatTime, type StatusLevel } from '@westhetics/ui'
import type { SessionLike } from './api'

/**
 * Textos e rótulos da conta (docs/areas/autenticacao.md, "Conta"). Valor cru de enum nunca vai
 * para a tela: a forma de entrada e o estado do acesso saem daqui.
 */

export type AccountTab = 'sessions' | 'connections' | 'password'

export const ACCOUNT_TABS: AccountTab[] = ['sessions', 'connections', 'password']

export const TAB_LABELS: Record<AccountTab, string> = {
  sessions: 'Acessos',
  connections: 'Aplicativos conectados',
  password: 'Senha',
}

/** As mensagens da senha, as mesmas de `/new-password` (RN-AUT-06). */
export const PASSWORD_MESSAGES = {
  current: 'Informe a senha atual.',
  weak: 'A senha precisa de pelo menos 8 caracteres, com letras e números.',
  same: 'A nova senha precisa ser diferente da atual.',
} as const

/** "Entrou com senha" · "Entrou com código por e-mail" · "Entrou pela conexão MCP · Claude Code". */
export function authMethodLabel(
  session: Pick<SessionLike, 'authMethod' | 'channel' | 'oauthClient'>,
): string {
  if (session.channel === 'mcp' || session.authMethod === 'oauth_provider/authorization_code') {
    const name = session.oauthClient?.name
    return name ? `Entrou pela conexão MCP · ${name}` : 'Entrou pela conexão MCP'
  }
  switch (session.authMethod) {
    case 'password':
      return 'Entrou com senha'
    case 'otp':
    case 'magiclink':
      return 'Entrou com código por e-mail'
    default:
      return 'Entrada sem forma registrada'
  }
}

/**
 * O estado do acesso: o desta requisição e os ainda ativos em ink; os encerrados em cinza, porque
 * são passado, não pendência. Nenhum é risco: plum não entra aqui.
 */
export function sessionStatus(session: Pick<SessionLike, 'isActive' | 'isCurrent'>): {
  label: string
  level: StatusLevel
} {
  if (session.isCurrent) return { label: 'Este dispositivo', level: 'done' }
  if (session.isActive) return { label: 'Ativo', level: 'done' }
  return { label: 'Encerrado', level: 'waiting' }
}

/** "28/09/2026 às 12:03": o registro de acesso pede data e hora (P-AUT-17). */
export function formatDateTime(date: Date) {
  return `${formatDate(date, { year: true })} às ${formatTime(date)}`
}

/**
 * Para compor "última atividade …" e "terminou …": tempo relativo até 7 dias ("há 2 horas"),
 * data e hora depois ("em 20/09/2026 às 18:40"), conforme docs/styleguide/04-escrita.md.
 */
export function formatWhen(date: Date, now = new Date()) {
  const relative = formatRelative(date, now)
  return /^\d/.test(relative) ? `em ${formatDateTime(date)}` : relative
}

/** A linha de contexto de um acesso; o IP só entra quando a API o devolveu (P-AUT-16). */
export function sessionMeta(session: SessionLike, options: { showClinic?: boolean } = {}) {
  const parts = [`${authMethodLabel(session)} em ${formatDateTime(new Date(session.startedAt))}`]
  if (session.ip) {
    parts.push(`IP ${session.ip}`)
    if (session.lastIp && session.lastIp !== session.ip) parts.push(`último IP ${session.lastIp}`)
  }
  if (session.endedAt) parts.push(`terminou ${formatWhen(new Date(session.endedAt))}`)
  else parts.push(`última atividade ${formatWhen(new Date(session.lastActiveAt))}`)
  if (options.showClinic && session.clinic) parts.push(session.clinic.name)
  return parts.join(' · ')
}
